import { requestLogout } from 'components/LogoutConfirm/LogoutConfirm';
import { FilePen, MessagesSquare, Search, Settings, Star, UserRound } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { RoleSwitcher } from 'components/Header/RoleSwitcher';
import { VerificationStatusBadge } from 'components/VerificationStatusBadge';
import { useProfileModeration } from 'hooks/useProfileModeration';
import { useVerificationEdits } from 'hooks/useVerificationEdits';
import { ChatReportMenuButton } from 'modules/ChatV2/components/ChatReportMenuButton';
import { useGlobalPresence } from 'modules/ChatV2/store/GlobalPresenceContext';
import { useGetCurrentProfile } from 'modules/Profile';
import { MenuItem } from 'ui/kit/Kit';
import kit from 'ui/kit/kit.module.scss';
import {
  markProfilePhotoFailed,
  profilePhotoFallback,
  profilePhotoSrc,
} from 'utils/profilePhotoSrc';

import classes from '../Teacher.module.scss';

export const TeacherMenu: React.FC = () => {
  const location = useLocation();
  const isActiveClass =
    location.pathname === '/teacher/search' ||
    location.pathname.startsWith('/teacher/search/error-');
  const { data } = useGetCurrentProfile();
  const { isUserOnline } = useGlobalPresence();
  const { isApproved } = useProfileModeration();
  const { hasRejectedItems } = useVerificationEdits();
  const currentUserId = data?.id_user ?? data?.id;
  const isOnline = isUserOnline(currentUserId);
  const [photoFailed, setPhotoFailed] = useState(false);

  const photoPath = useMemo(() => {
    if (photoFailed) return profilePhotoFallback();
    return profilePhotoSrc(data?.profile_photo_token) || profilePhotoFallback();
  }, [data?.profile_photo_token, photoFailed]);

  return (
    <aside className={classes['teacher-menu']}>
      <div className={classes['teacher-menu__header']}>
        <div className={classes['teacher-menu__header-content']}>
          <div className={classes['teacher-menu__avatar']}>
            <img
              src={photoPath}
              alt=''
              onError={() => {
                markProfilePhotoFailed(data?.profile_photo_token);
                setPhotoFailed(true);
              }}
            />
            {isOnline && <i />}
          </div>
          <div className={classes['teacher-menu__info']}>
            <span className={classes['teacher-menu__name']}>{data?.name}</span>
            <span className={classes['teacher-menu__role']}>Репетитор</span>
          </div>
        </div>
        {/* Проверка паспорта временно отключена
        {data?.profile_verified ? (
          <span
            className={classes['teacher-menu__status']}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span className={classes['teacher-menu__status-icon']}>
              <AcceptIconTeacherActive />
            </span>
            <span style={{ display: 'block', paddingTop: '9px', paddingLeft: '3px' }}>
              Документы подтверждены
            </span>
          </span>
        ) : (
          <span className={classes['teacher-menu__status']}>
            <span className={classes['teacher-menu__status-icon']}>
              <AcceptIconTeacher />
            </span>
            <span style={{ display: 'block', paddingTop: '3px' }}>
              Документы не подтверждены
            </span>
          </span>
        )}
        */}
        <VerificationStatusBadge
          status={data?.verification_status}
          comment={data?.verification_comment}
          place='right'
        />
      </div>

      <div className={classes['teacher-menu__role-switcher']}>
        <RoleSwitcher variant='menu' />
      </div>

      <nav className={classes['teacher-menu__nav']} aria-label='Разделы кабинета'>
        <MenuItem to='/teacher' end label='Мой профиль' icon={<UserRound />} />
        {hasRejectedItems ? (
          <MenuItem to='/teacher/edits' label='Редактирования' icon={<FilePen />} />
        ) : null}
        <MenuItem
          to='/teacher/search'
          label='Поиск'
          icon={<Search />}
          active={isActiveClass}
          locked={!isApproved}
        />
        <MenuItem
          to='/teacher/chat'
          label='Чаты и отклики'
          icon={<MessagesSquare />}
          locked={!isApproved}
        />
        <MenuItem to='/teacher/settings' label='Настройки' icon={<Settings />} />
        {/* «Рейтинг» возвращен в меню (был в HEAD, Figma 3:30077). */}
        <MenuItem to='/teacher/trust-score' label='Рейтинг' icon={<Star />} />
        {!isApproved ? (
          <p className={kit.menuHint}>Поиск и чаты доступны после одобрения профиля.</p>
        ) : null}
      </nav>
      <hr className={kit.menuDivider} />
      <nav className={kit.menuSecondaryGroup} aria-label='Помощь и выход'>
        <MenuItem to='/teacher/questions' label='Частые вопросы' secondary />
        <MenuItem to='/teacher/help' label='Тех.поддержка' secondary />
        <ChatReportMenuButton className={`${kit.menu} ${kit.menuSecondary}`} />
        <MenuItem label='Выход' onClick={() => requestLogout()} secondary />
      </nav>
    </aside>
  );
};
